import React, { useEffect } from "react";
import Sidebar from "../shared/components/Sidebar/Sidebar";
import { useUser } from "../auth/hooks/useUser";
import { useLocation } from "react-router";
import { useSkinData } from "../services/common/hooks/useSkinData";
import useFavicon from "../shared/hooks/useFavicon";
import useDynamicManifest from "../shared/hooks/useDynamicManifest";
import FullPageSpinner from "../shared/components/spinner/FullPageSpinner";
import FullPageError from "../shared/components/errors/FullPageError";
import { useGlobalShortcuts } from "../shared/hooks/useGlobalShortcuts";

interface AdminLayoutProps {
    children: React.ReactNode;
}

const baseUrl = window.location.origin; // Get the base URL from the current window location
const base64Url = btoa(baseUrl); // Encode the base URL in base64 format

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
    const { refetch } = useUser();
    const location = useLocation();

    // Initialize global keyboard shortcuts
    useGlobalShortcuts();

    const { data, isLoading, isError } = useSkinData(base64Url);

    useFavicon(data?.data?.favicon_url || null);

    // Ambil warna dari skinData, jika tidak ada, gunakan warna default
    const themeColor = data?.data?.color || '#076a4e';

    // PWA "Add to Home Screen" ikut branding whitelabel (bukan default statis "Djitu")
    useDynamicManifest({
        name: data?.data?.name,
        logoUrl: data?.data?.logo_url,
        themeColor,
    });

    useEffect(() => {
        // Pilih elemen root dari dokumen (<html>)
        const root = document.documentElement;

        // Setel nilai variabel CSS --tblr-primary secara dinamis
        root.style.setProperty('--tblr-primary', themeColor);

        document.title = data?.data?.name || 'Admin Panel';

        // Cleanup function (opsional, tapi praktik yang baik)
        // Ini akan mengembalikan warna ke default jika komponen di-unmount
        return () => {
            root.style.removeProperty('--tblr-primary');

            document.title = 'Admin Panel'; // Reset title to default on unmount
        };
    }, [themeColor, data]);

    useEffect(() => {
        if (!location.key) {
            refetch();
        }
    }, []);

    if (isLoading) {
        return <FullPageSpinner />; // You can replace this with a spinner or loading component
    }

    if (isError) {
        return <FullPageError message="Failed to load skin data. Please try again later." />;
    }

    return (
        <div className="page">
            <Sidebar skinData={data?.data}>
                <div className="page-wrapper">
                    {children}
                </div>
            </Sidebar>
        </div>
    );
};

export default AdminLayout;